import React, { useEffect, useRef } from 'react';

/** How long a picture stays solid before it starts turning into the next one. */
export const FRAME_HOLD_MS = 1100;
/** How long the dissolve itself lasts. Long enough to read as a fade, not a cut. */
export const FRAME_FADE_MS = 1800;

function motionReduced(): boolean {
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Smooth 0..1 curve so the fade eases in and out instead of stepping. */
export function smoothstep(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
}

/**
 * The picture that is leaving stays fully visible. The next one fades in on top.
 * At the end both are opaque; the one underneath can then drop away unseen.
 */
export function dissolveOpacities(count: number, from: number, to: number, t: number): number[] {
  const opacities = Array.from({ length: count }, () => 0);
  if (count <= 0) return opacities;
  opacities[Math.min(Math.max(from, 0), count - 1)] = 1;
  if (count === 1 || from === to) return opacities;
  opacities[((to % count) + count) % count] = smoothstep(t);
  return opacities;
}

export type DissolveClock = {
  from: number;
  phase: 'hold' | 'fade';
  phaseStart: number;
};

export function initialDissolve(now: number): DissolveClock {
  return { from: 0, phase: 'hold', phaseStart: now };
}

/** Moves the clock forward. A long pause can cross more than one phase. */
export function advanceDissolve(clock: DissolveClock, count: number, now: number): DissolveClock {
  if (count < 2) return { from: 0, phase: 'hold', phaseStart: now };
  let current = clock;
  for (let i = 0; i < 8; i += 1) {
    const elapsed = now - current.phaseStart;
    if (current.phase === 'hold' && elapsed >= FRAME_HOLD_MS) {
      current = { from: current.from, phase: 'fade', phaseStart: current.phaseStart + FRAME_HOLD_MS };
      continue;
    }
    if (current.phase === 'fade' && elapsed >= FRAME_FADE_MS) {
      current = {
        from: (current.from + 1) % count,
        phase: 'hold',
        phaseStart: current.phaseStart + FRAME_FADE_MS,
      };
      continue;
    }
    break;
  }
  return current;
}

export function opacitiesFor(clock: DissolveClock, count: number, now: number): number[] {
  if (count <= 0) return [];
  if (count === 1) return [1];
  const to = (clock.from + 1) % count;
  if (clock.phase === 'hold') return dissolveOpacities(count, clock.from, to, 0);
  return dissolveOpacities(count, clock.from, to, (now - clock.phaseStart) / FRAME_FADE_MS);
}

/** The incoming picture sits above the one it is dissolving into. */
export function topLayer(clock: DissolveClock, count: number): number {
  if (count <= 1) return 0;
  if (clock.phase === 'fade') return (clock.from + 1) % count;
  return clock.from;
}

/**
 * Pictures the generator will use, in play order.
 * Uploads on the card win. Otherwise the images already linked to the video.
 */
export function generatorPreviewUrls(
  cardUrls: Array<string | undefined>,
  refUrls: Array<string | undefined> | undefined,
): string[] {
  const own = cardUrls.filter((url): url is string => Boolean(url));
  if (own.length) return own;
  return (refUrls || []).filter((url): url is string => Boolean(url));
}

function paintFrames(
  root: HTMLElement | null,
  opacities: number[],
  top: number,
) {
  if (!root) return;
  const nodes = root.querySelectorAll('img');
  nodes.forEach((img, i) => {
    if (i >= opacities.length) return;
    const opacity = opacities[i] ?? 0;
    img.style.opacity = opacity.toFixed(3);
    img.style.zIndex = String(i === top && opacity > 0 ? 2 : opacity > 0 ? 1 : 0);
  });
}

/** Dissolves the frames in order so the empty generator suggests the cut. */
export default function FrameCrossfade({ urls }: { urls: string[] }) {
  const count = urls.length;
  const key = urls.join('\n');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (count < 2 || motionReduced()) {
      paintFrames(root, urls.map((_, i) => (i === 0 ? 1 : 0)), 0);
      return undefined;
    }

    let clock = initialDissolve(performance.now());
    let raf = 0;
    let stopped = false;
    const step = (now: number) => {
      if (stopped) return;
      clock = advanceDissolve(clock, count, now);
      paintFrames(root, opacitiesFor(clock, count, now), topLayer(clock, count));
      raf = window.requestAnimationFrame(step);
    };
    raf = window.requestAnimationFrame(step);
    return () => {
      stopped = true;
      window.cancelAnimationFrame(raf);
    };
    // `key` already captures the url list. Depending on the array would restart the fade every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, key]);

  return (
    <div className="tb-gen-crossfade" aria-hidden="true" ref={rootRef}>
      {urls.map((url, i) => (
        <img
          key={`${i}:${url}`}
          src={url}
          alt=""
          draggable={false}
          style={{ opacity: i === 0 ? 1 : 0, zIndex: i === 0 ? 1 : 0 }}
        />
      ))}
    </div>
  );
}
